import { animate } from 'animejs';

// ============================================================
// Player reveal animations (used on player screen)
// ============================================================

const REVEAL_MS = 2800;

export type AnimationPreset = (
  el: HTMLElement,
  opts?: { duration?: number }
) => ReturnType<typeof animate>;

/** Flash in top-left corner then fade out (moderate zoom/offset to avoid cut-off) */
export const flashCornerTL: AnimationPreset = (el, opts = {}) => {
  const duration = opts.duration ?? REVEAL_MS;
  el.style.transform = 'translate(-12%, -12%) scale(0.82)';
  el.style.opacity = '0';
  return animate(
    el,
    {
      opacity: [0, 1, 0],
      translateX: ['-12%', '0%', '-12%'],
      translateY: ['-12%', '0%', '-12%'],
      scale: [0.82, 1, 0.82],
      duration,
      easing: 'easeOutQuad',
    } as any
  );
};

/** Flash in bottom-right then fade (moderate zoom/offset to avoid cut-off) */
export const flashCornerBR: AnimationPreset = (el, opts = {}) => {
  const duration = opts.duration ?? REVEAL_MS;
  el.style.transform = 'translate(12%, 12%) scale(0.82)';
  el.style.opacity = '0';
  return animate(
    el,
    {
      opacity: [0, 1, 0],
      translateX: ['12%', '0%', '12%'],
      translateY: ['12%', '0%', '12%'],
      scale: [0.82, 1, 0.82],
      duration,
      easing: 'easeOutQuad',
    } as any
  );
};

/** Blur in then blur out */
export const blurReveal: AnimationPreset = (el, opts = {}) => {
  const duration = opts.duration ?? REVEAL_MS;
  el.style.filter = 'blur(20px)';
  el.style.opacity = '0';
  return animate(
    el,
    {
      opacity: [0, 1, 0],
      filter: ['blur(20px)', 'blur(0px)', 'blur(20px)'],
      duration,
      easing: 'easeOutQuad',
    } as any
  );
};

/** Rotate in then fade out */
export const rotateReveal: AnimationPreset = (el, opts = {}) => {
  const duration = opts.duration ?? REVEAL_MS;
  el.style.transform = 'rotate(-180deg)';
  el.style.opacity = '0';
  return animate(
    el,
    {
      opacity: [0, 1, 0],
      rotate: ['-180deg', '0deg', '180deg'],
      duration,
      easing: 'easeOutQuad',
    } as any
  );
};

/** Low opacity peek then fade */
export const opacityPeek: AnimationPreset = (el, opts = {}) => {
  const duration = opts.duration ?? REVEAL_MS;
  el.style.opacity = '0';
  return animate(
    el,
    {
      opacity: [0, 0.5, 0.15, 0],
      duration,
      easing: 'easeInOutQuad',
    } as any
  );
};

/** Zoom in then zoom out and fade */
export const zoomPulse: AnimationPreset = (el, opts = {}) => {
  const duration = opts.duration ?? REVEAL_MS;
  el.style.transform = 'scale(0.3)';
  el.style.opacity = '0';
  return animate(
    el,
    {
      opacity: [0, 1, 0],
      scale: [0.3, 1.2, 0.5],
      duration,
      easing: 'easeOutQuad',
    } as any
  );
};

const POOL: AnimationPreset[] = [
  flashCornerTL,
  flashCornerBR,
  blurReveal,
  rotateReveal,
  opacityPeek,
  zoomPulse,
];

export function pickRandomRevealAnimation(): AnimationPreset {
  return POOL[Math.floor(Math.random() * POOL.length)];
}

// ============================================================
// Host animations: each runs once for a set duration (2s default).
// The animation controls opacity: 0 → visible peak → 0.
// No loop — the caller manages the cycle (animate → break → repeat).
// ============================================================

export type HostAnimationFn = (
  el: HTMLElement,
  opts?: { duration?: number }
) => ReturnType<typeof animate>;

/** Blur peek: fade in heavily blurred, briefly clear, fade out blurred */
export const hostBlurPeek: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  return animate(el, {
    opacity: [0, 0.7, 0.85, 0.7, 0],
    filter: ['blur(30px)', 'blur(10px)', 'blur(3px)', 'blur(10px)', 'blur(30px)'],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Scale bounce: grow from tiny, briefly full size, shrink back */
export const hostScaleBounce: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  return animate(el, {
    opacity: [0, 0.75, 0.9, 0.75, 0],
    scale: [0.15, 0.85, 1, 0.85, 0.15],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Rotate flash: spin in while fading in, hold, spin out while fading */
export const hostRotateFlash: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  return animate(el, {
    opacity: [0, 0.8, 0.85, 0.8, 0],
    rotate: ['-180deg', '-30deg', '0deg', '30deg', '180deg'],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Flicker: rapid glitchy on/off flashes */
export const hostFlicker: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  return animate(el, {
    opacity: [0, 0.7, 0.1, 0.8, 0.15, 0.75, 0.1, 0.65, 0],
    duration: d,
    easing: 'linear',
  } as any);
};

/** Slide from top-left corner: enter diagonally, briefly visible, exit */
export const hostSlideTL: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  return animate(el, {
    opacity: [0, 0.8, 0.85, 0.8, 0],
    translateX: ['-40%', '-5%', '0%', '5%', '40%'],
    translateY: ['-40%', '-5%', '0%', '5%', '40%'],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Slide from bottom-right: enter diagonally, briefly visible, exit */
export const hostSlideBR: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  return animate(el, {
    opacity: [0, 0.8, 0.85, 0.8, 0],
    translateX: ['40%', '5%', '0%', '-5%', '-40%'],
    translateY: ['40%', '5%', '0%', '-5%', '-40%'],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Breathe: gentle scale + opacity pulse centered */
export const hostBreathe: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  return animate(el, {
    opacity: [0, 0.6, 0.8, 0.6, 0],
    scale: [0.85, 0.95, 1.05, 0.95, 0.85],
    duration: d,
    easing: 'easeInOutSine',
  } as any);
};

/** Zoom blur: start zoomed in close (blurred), zoom out to reveal, zoom back in */
export const hostZoomBlur: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  return animate(el, {
    opacity: [0, 0.7, 0.85, 0.7, 0],
    scale: [2.5, 1.3, 1, 1.3, 2.5],
    filter: ['blur(20px)', 'blur(5px)', 'blur(0px)', 'blur(5px)', 'blur(20px)'],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Zoom subtle: scale 0.2 → 0.8 peak then back (gentle zoom range) */
export const hostZoomSubtle: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  el.style.transform = 'scale(0.2)';
  return animate(el, {
    opacity: [0, 0.7, 0.85, 0.7, 0],
    scale: [0.2, 0.8, 0.8, 0.5, 0.2],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Rotate 45° left then right: -45deg → 0 → 45deg with opacity */
export const hostRotate45: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  return animate(el, {
    opacity: [0, 0.75, 0.85, 0.75, 0],
    rotate: ['-45deg', '0deg', '45deg'],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Horizontal flip: rotateY 0 → 360deg (full flip and back) */
export const hostFlipHorizontal: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  el.style.transformOrigin = 'center center';
  return animate(el, {
    opacity: [0, 0.8, 0.85, 0.8, 0],
    rotateY: ['0deg', '180deg', '360deg'],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Slide left to right while blinking */
export const hostSlideBlink: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  el.style.transform = 'translateX(-60%)';
  return animate(el, {
    opacity: [0, 0.8, 0.2, 0.85, 0.15, 0.75, 0],
    translateX: ['-60%', '-20%', '0%', '20%', '60%'],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Slide in from left, cross center, exit right with blink */
export const hostSlideLeftRight: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  el.style.transform = 'translateX(-70%)';
  return animate(el, {
    opacity: [0, 0.7, 0.2, 0.8, 0.2, 0.7, 0],
    translateX: ['-70%', '-35%', '0%', '35%', '70%'],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Shutter reveal: clip from top (blind) then reveal, then close */
export const hostShutterReveal: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  el.style.clipPath = 'inset(100% 0 0 0)';
  return animate(el, {
    opacity: [0, 0.9, 0.9, 0],
    clipPath: [
      'inset(100% 0 0 0)',
      'inset(0% 0 0 0)',
      'inset(0% 0 0 0)',
      'inset(100% 0 0 0)',
    ],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

/** Block reveal: center expands to full then shrinks (inset) */
export const hostBlockReveal: HostAnimationFn = (el, opts = {}) => {
  const d = opts.duration ?? 2000;
  el.style.clipPath = 'inset(50% 50% 50% 50%)';
  return animate(el, {
    opacity: [0, 0.85, 0.85, 0],
    clipPath: [
      'inset(50% 50% 50% 50%)',
      'inset(0% 0% 0% 0%)',
      'inset(0% 0% 0% 0%)',
      'inset(50% 50% 50% 50%)',
    ],
    duration: d,
    easing: 'easeInOutQuad',
  } as any);
};

const HOST_ANIM_POOL: HostAnimationFn[] = [
  hostBlurPeek,
  hostScaleBounce,
  hostRotateFlash,
  hostFlicker,
  hostSlideTL,
  hostSlideBR,
  hostBreathe,
  hostZoomBlur,
  hostZoomSubtle,
  hostRotate45,
  hostFlipHorizontal,
  hostSlideBlink,
  hostSlideLeftRight,
  hostShutterReveal,
  hostBlockReveal,
];

/** Pick a random host animation (different each call) */
export function getRandomHostAnimation(): HostAnimationFn {
  return HOST_ANIM_POOL[Math.floor(Math.random() * HOST_ANIM_POOL.length)];
}
